import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import SortOutlined from '@mui/icons-material/SortOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { P, type PermissionKey } from '../../utils/permissions';
import { scopeMeta } from '../../utils/permissionCatalogue';

export type SettingsSectionId = 'stages' | 'types' | 'fields' | 'templates' | 'rate-cards';

/** `read-only`: shown with no write control. `locked`: shown and explained, fetching nothing. */
export type SectionState = 'usable' | 'read-only' | 'locked';

export interface SettingsSection {
  id: SettingsSectionId;
  label: string;
  icon: ComponentType<SvgIconProps>;
  /** What the section is for, as the locked overview lists it. */
  purpose: string;
  /** The key a locked caller is told they need. */
  requiredPermission: PermissionKey;
  state: SectionState;
}

export interface ProjectSettingsAccess {
  sections: SettingsSection[];
  /** Every section is locked, so the tab shows the overview instead of a strip. */
  tabLocked: boolean;
  /** The first section the caller can open; the first section when all are locked. */
  defaultSectionId: SettingsSectionId;
}

type Has = (key: string) => boolean;

interface SectionDefinition extends Omit<SettingsSection, 'state'> {
  stateFor: (has: Has) => SectionState;
}

const usableWith = (key: PermissionKey) => (has: Has): SectionState =>
  has(key) ? 'usable' : 'locked';

/**
 * Each gate mirrors the server's: the section's writes are refused without
 * its key, and its reads are what a locked section never makes. Stages and
 * types have their own keys, not `manage:settings`.
 *
 * Rate cards read under `view:rates:charge` and write under that key plus
 * `manage:rate-cards`, so `manage:rate-cards` alone opens nothing and the
 * section names View Charge Rates as the key to ask for.
 */
const SECTIONS: readonly SectionDefinition[] = [
  {
    id: 'stages',
    label: 'Stages',
    icon: SortOutlined,
    purpose: 'The pipeline every project moves through',
    requiredPermission: P.MANAGE_PROJECT_STAGES,
    stateFor: usableWith(P.MANAGE_PROJECT_STAGES),
  },
  {
    id: 'types',
    label: 'Types',
    icon: LocalOfferOutlined,
    purpose: 'How a project is sold',
    requiredPermission: P.MANAGE_PROJECT_TYPES,
    stateFor: usableWith(P.MANAGE_PROJECT_TYPES),
  },
  {
    id: 'fields',
    label: 'Custom fields',
    icon: TuneOutlined,
    purpose: 'Extra details recorded on every project',
    requiredPermission: P.MANAGE_METADATA,
    stateFor: usableWith(P.MANAGE_METADATA),
  },
  {
    id: 'templates',
    label: 'Detail templates',
    icon: DescriptionOutlined,
    purpose: 'Starting text for a project’s details',
    requiredPermission: P.MANAGE_METADATA,
    stateFor: usableWith(P.MANAGE_METADATA),
  },
  {
    id: 'rate-cards',
    label: 'Rate cards',
    icon: ReceiptLongOutlined,
    purpose: 'Day rates projects are charged at',
    requiredPermission: P.VIEW_RATES_CHARGE,
    stateFor: (has) => {
      if (!has(P.VIEW_RATES_CHARGE)) return 'locked';
      return has(P.MANAGE_RATE_CARDS) ? 'usable' : 'read-only';
    },
  },
];

/**
 * The caller's view of Projects → Settings: every section, each usable,
 * read-only or locked. Locked sections stay listed — the *locked and
 * explained* rule in docs/architecture/design-system.md (Locked navigation).
 */
export function buildProjectSettingsAccess(has: Has): ProjectSettingsAccess {
  const sections = SECTIONS.map(({ stateFor, ...section }) => ({
    ...section,
    state: stateFor(has),
  }));
  const firstOpen = sections.find((s) => s.state !== 'locked');
  return {
    sections,
    tabLocked: !firstOpen,
    defaultSectionId: (firstOpen ?? sections[0]).id,
  };
}

/**
 * The section a `sub` opens. A locked section's link opens its locked panel
 * rather than redirecting; an unknown or absent one opens the default.
 */
export function resolveActiveSection(
  access: ProjectSettingsAccess,
  sub: string | null,
): SettingsSection {
  return (
    access.sections.find((s) => s.id === sub) ??
    access.sections.find((s) => s.id === access.defaultSectionId)!
  );
}

/** The tooltip, the persistent description and the locked panel all read this sentence. */
export function lockedReason(section: Pick<SettingsSection, 'requiredPermission'>): string {
  return `Needs the ${scopeMeta(section.requiredPermission).displayName} permission. An administrator can grant it.`;
}

/** The all-locked overview's heading. */
export const SETTINGS_LOCKED_TITLE = "Project settings need a permission you don’t have";

/** The locked Settings tab's tooltip and description. */
export const SETTINGS_TAB_LOCKED_REASON = `${SETTINGS_LOCKED_TITLE}. An administrator can grant it.`;
